import {collection, doc, DocumentSnapshot, getDoc} from "firebase/firestore";
import {db} from "@/db";
import {callApi} from "@/api";
import moment from "moment";
import {Session} from "@/models/User";
import {writeRepository} from "@/data/writeRepository";
import deepMerge from "@strenforcement/domain/deepMerge";

/**
 * Base model class for providing a consistent interface to a document in Firestore.
 */
export class PersistentModel {

  static get collectionName() {
    let className = this.name;
    let collectionName = `${className[0].toLowerCase()}${className.substring(1)}`;
    if (collectionName.endsWith('s')) {
      return `${collectionName}es`;
    } else if (collectionName.endsWith('y')) {
      return collectionName.replace(/y$/, 'ies');
    } else {
      return `${collectionName}s`;
    }
  }

  constructor(doc) {
    if (!doc) {
      this._docRef = null;
      this._data = {};
    } else if (doc instanceof DocumentSnapshot) {
      this.doc = doc;
    } else {
      this._docRef = doc.ref;
      this._data = doc.data || {};
    }
    this.requests = [];
    this.errors = [];
    this.updateCount = 0;
  }

  /** Preserve the legacy hook without printing record contents from its callers. */
  log() {
    // Callers may still pass private record values; intentionally omit browser diagnostics.
  }

  toMoment(dateOrStringOrTimestamp) {
    if (dateOrStringOrTimestamp) {
      if (typeof dateOrStringOrTimestamp === 'string') {
        return moment(dateOrStringOrTimestamp, 'YYYY-MM-DD');
      } else if (dateOrStringOrTimestamp instanceof Date) {
        return moment(dateOrStringOrTimestamp);
      } else if (dateOrStringOrTimestamp.toDate) {
        return moment(dateOrStringOrTimestamp.toDate());
      }
    }
    return null;
  }

  get db() {
    return this._docRef ? this._docRef.firestore : null;
  }

  set doc(doc) {
    this._docRef = doc.ref;
    this._data = doc.data() || {};
  }

  get doc() {
    return this._docRef;
  }

  get docPath() {
    return this._docRef?.path || null;
  }

  get writeRoutePrefix() {
    return this.constructor.collectionName || this.constructor.name;
  }

  getWriteRouteKey(operation) {
    return `${this.writeRoutePrefix}.${operation}`;
  }

  async collection() {
    if (this._docRef) {
      return Promise.resolve(this._docRef.parent);
    } else {
      return collection(await db, this.constructor.collectionName);
    }
  }

  reload() {
    if (this._docRef) {
      return getDoc(this._docRef)
      .then(doc => this.doc = doc)
      .then(() => this);
    } else {
      return Promise.resolve(this);
    }
  }

  async update(data, action, updateFirst) {
    const docPath = this.docPath;
    if (!docPath) {
      throw new Error(`Could not update ${this.constructor.name}: no document path`);
    }

    const dataBefore = { ...this._data };
    if (updateFirst) {
      deepMerge(this._data, data);
    }
    try {
      await writeRepository.patchDoc(docPath, data, {
        merge: true,
        routeKey: this.getWriteRouteKey('patchDoc')
      });
      if (!updateFirst) {
        deepMerge(this._data, data);
      }
    } catch (error) {
      console.error('Could not update record', error);
      this._data = dataBefore;
    }

    this.updateCount++;
    return this;
  }

  copyFrom(fromModel) {
    this._data = JSON.parse(JSON.stringify(fromModel._data));
    return this.save();
  }

  async save() {
    if (this._docRef) {
      await writeRepository.setDoc(this.docPath, this._data, {
        merge: true,
        routeKey: this.getWriteRouteKey('setDoc')
      });
    } else {
      const col = await this.collection();
      const created = await writeRepository.createDoc(col.path, this._data, {
        routeKey: this.getWriteRouteKey('createDoc')
      });
      const path = created?.path || (created?.id ? `${col.path}/${created.id}` : null);
      if (!path) {
        throw new Error(`Could not save ${this.constructor.name}: write repository did not return created document path`);
      }
      this._docRef = doc(await db, path);
    }
    return this;
  }

  async delete() {
    const docPath = this.docPath;
    if (!docPath) {
      throw new Error(`Could not delete ${this.constructor.name}: no document path`);
    }
    await writeRepository.deleteDoc(docPath, {
      routeKey: this.getWriteRouteKey('deleteDoc')
    });
  }


  _get(field) {
    const fieldPath = field.split(/\./g);
    let value = this._data;
    fieldPath.forEach(field => {
      value = value && value[field];
    });
    return value;
  }

  _set(field, value) {
    const fieldPath = field.split(/\./g);
    let parent = this._data;
    for (let i = 0; i < fieldPath.length; i++) {
      if (i === fieldPath.length - 1) {
        parent[fieldPath[i]] = value;
      } else {
        parent = parent[fieldPath[i]] || {};
      }
    }
  }

  get isNew() {
    return !this.id;
  }

  get id() {
    return this._docRef ? this._docRef.id : null;
  }

  get data() {
    return this._data;
  }

  get hasData() {
    return !!this.data && Object.keys(this.data).length > 0;
  }

  copy() {
    return new this.constructor(this.exportForCopy());
  }

  exportForCopy() {
    return {
      ref: this._docRef,
      data: JSON.parse(JSON.stringify(this._data))
    };
  }

  indexKeys() {
    return [];
  }

  subCollection(collectionName) {
    return collection(this._docRef, collectionName);
  }

  subDoc(collectionName, docId) {
    return doc(this.db, `${this._docRef.path}/${collectionName}/${docId}`);
  }

  isRequesting(name) {
    return this.requests.indexOf(name) >= 0;
  }

  doApiCall(name, data) {
    this.requests.push(name);
    data = data || {};
    data[this.idFieldNameForApi] = this.id;
    return callApi(name, data)
    .then(() => this.reload())
    .catch(err => {
      console.warn('An API request failed', err);
      this.errors.push({ name, error: err, date: new Date() });
    })
    .finally(() => this.requests = this.requests.filter(request => request !== name));
  }

  get idFieldNameForApi() {
    return 'id';
  }

  get currentUser() {
    const currentUser = Session.currentUser.value;
    if (currentUser) {
      return {
        id: currentUser.id,
        displayName: currentUser.displayName,
        email: currentUser.email
      };
    } else {
      return null;
    }
  }

}
